/**
 * CrisisHelpFooter — quiet helpline footer.
 *
 * iter-80: surfaces region-aware crisis resources when the backend
 * `crisis_flag` is true on a moodcard. The footer is intentionally
 * subdued (no big banner, no panic styling) — just an honest line
 * that says "if you need to talk, here's who picks up."
 *
 * The locality-to-hotline map is conservative; falls back to a
 * multi-region generic line when locality isn't recognized.
 */
import React from "react";
import { motion } from "framer-motion";
import { LifeBuoy } from "lucide-react";

const HOTLINES = {
    US: { label: "988", detail: "call or text · suicide & crisis lifeline" },
    UK: { label: "Samaritans 116 123", detail: "free · 24/7 · UK & Ireland" },
    KE: { label: "+254 722 178177", detail: "befrienders kenya · 24/7" },
    NG: { label: "+234 806 210 6493", detail: "she writes woman · nigeria" },
    IN: { label: "iCALL 9152987821", detail: "free · english/hindi · 8am-10pm ist" },
    CA: { label: "9-8-8", detail: "call or text · canada suicide crisis helpline" },
    AU: { label: "Lifeline 13 11 14", detail: "free · 24/7 · australia" },
};

function pickHotline(locality) {
    if (!locality) return null;
    const loc = locality.toLowerCase();
    if (/nairobi|kenya|mombasa|kisumu|nakuru/.test(loc)) return HOTLINES.KE;
    if (/lagos|abuja|nigeria|ibadan|port harcourt/.test(loc)) return HOTLINES.NG;
    if (/uk|london|manchester|birmingham|leeds|scotland|ireland/.test(loc)) return HOTLINES.UK;
    if (/india|mumbai|delhi|bangalore|bengaluru|chennai|kolkata|kerala/.test(loc)) return HOTLINES.IN;
    if (/canada|toronto|montreal|vancouver|ottawa/.test(loc)) return HOTLINES.CA;
    if (/australia|sydney|melbourne|brisbane|perth/.test(loc)) return HOTLINES.AU;
    if (/us|usa|america|new york|los angeles|chicago|atlanta|miami|texas/.test(loc)) return HOTLINES.US;
    return null;
}

export default function CrisisHelpFooter({ show, locality }) {
    if (!show) return null;
    const hotline = pickHotline(locality);

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 mx-auto max-w-md rounded-2xl border border-emerald-300/15 bg-emerald-400/5 px-4 py-3"
            data-testid="crisis-help-footer"
        >
            <div className="flex items-start gap-3">
                <div className="w-7 h-7 shrink-0 rounded-full bg-emerald-400/10 border border-emerald-300/25 flex items-center justify-center mt-0.5">
                    <LifeBuoy className="w-3.5 h-3.5 text-emerald-300" strokeWidth={1.7} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] tracking-[0.22em] uppercase text-emerald-200/85 mb-0.5">
                        if you need to talk
                    </div>
                    {hotline ? (
                        <>
                            <div className="font-serif italic text-emerald-50 text-sm leading-snug">
                                {hotline.label}
                            </div>
                            <div className="text-[11px] text-emerald-100/55 leading-snug mt-0.5">
                                {hotline.detail}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="font-serif italic text-emerald-50 text-sm leading-snug">
                                you're not alone tonight.
                            </div>
                            <div className="text-[11px] text-emerald-100/55 leading-snug mt-0.5">
                                US/CA: call or text 988 · UK/IRE: Samaritans 116 123 · or your local crisis line.
                            </div>
                        </>
                    )}
                </div>
            </div>
        </motion.div>
    );
}
